import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import type {
  EnhancedConfigTypeId,
  EnhancedLanguageId,
  EnhancedManCode,
  EnhancedMarketId,
  EnhancedVehicleTypeId,
  EnhancedViewId,
} from '#api/enhancements/enhancedTmsApiTypes.ts'
import type { ManCode, VehicleConfiguration } from '#api/tmsApiService'
import { BreakAtDividers } from '#components/tabularOverlay/EntryHelpers'
import type { InfoGroup } from '#components/tabularOverlay/InfoEntry'
import type { FullApiConfiguration } from '#features/Configuration/ApiConfiguration'
import { extractTitle } from '#features/Configuration/ConfigurationTitle'
import { useFullApiConfigurationInfo } from '#features/Configuration/useFullApiConfigurationInfo'
import type { ConfigurationStorageDataV1 } from '#features/ManCode/ConfigurationStorageDataV1'
import { isValidPeriod, isValidPricingDate, periodToString, pricingDateToString } from '#utils/DateString'
import { formatByteSize } from '#utils/formatByteSize'
import type { ConfigurationStorageDataV2 } from './ConfigurationStorageDataV2'
import { readInternalStoredData } from './readInternalStoredData'

type WithRequired<T, K extends keyof T> = T & { [P in K]-?: T[P] }
type CompleteManCode = WithRequired<ManCode, 'configuration' | 'code'> & {
  configuration: WithRequired<VehicleConfiguration, 'ecmParameters'>
  reducedVersion: boolean
}

function ensureManCode(manCode: ManCode): manCode is CompleteManCode {
  return !!manCode.configuration && !!manCode.code && !!manCode.configuration.ecmParameters
}

export function useManCodeTitle(manCode: ManCode) {
  return useMemo(() => {
    if (!ensureManCode(manCode)) {
      return manCode.code ?? 'TMS-INVALID'
    }
    const internalDetails = readInternalStoredData(manCode)
    const storedTitle = internalDetails?.title ?? undefined
    const { configurationTitle, defaultConfigurationTitle } = extractTitle(storedTitle)
    return [manCode.code, configurationTitle !== defaultConfigurationTitle ? configurationTitle : undefined].filter(Boolean).join(' | ')
  }, [manCode])
}

function useMaicInternalInfo(storedConfiguration: ConfigurationStorageDataV1 | ConfigurationStorageDataV2 | undefined): InfoGroup {
  const { t } = useTranslation('shared')

  const storedTitle = storedConfiguration?.title ?? undefined
  const { configurationTitle, defaultConfigurationTitle } = extractTitle(storedTitle)

  return {
    title: t('man-code.information-overlay.maic-internal.title', 'MAIC Internal'),
    groupId: 'maic-internal',
    entries: [
      {
        title: t('man-code.information-overlay.maic-internal.user-title', 'Title'),
        entryId: 'user-title',
        value: configurationTitle !== defaultConfigurationTitle ? configurationTitle : undefined,
      },
      {
        title: t('man-code.information-overlay.maic-internal.data-version', 'Data Version'),
        entryId: 'data-version',
        value: storedConfiguration?.version,
      },
      {
        title: t('man-code.information-overlay.maic-internal.configuration-version', 'Configuration Version'),
        entryId: 'configuration-version',
        value: String(storedConfiguration?.configurationVersion ?? 0),
      },
    ],
  }
}

const useMetaDataInfo = (manCode: CompleteManCode): InfoGroup => {
  const { t } = useTranslation('shared')

  const sizeInBytes = useMemo(() => {
    return new Blob([JSON.stringify(manCode)]).size
  }, [manCode])

  return {
    title: t('man-code.information-overlay.meta-data.title', 'Meta Data'),
    groupId: 'meta-data',
    entries: [
      {
        title: t('man-code.information-overlay.meta-data.insert-date', 'Insert Date'),
        entryId: 'insert-date',
        value: manCode.insertTimestamp,
        displayValue: <BreakAtDividers>{manCode.insertTimestamp}</BreakAtDividers>,
      },
      {
        title: t('man-code.information-overlay.meta-data.update-date', 'Update Date'),
        entryId: 'update-date',
        value: manCode.updateTimestamp,
        displayValue: <BreakAtDividers>{manCode.updateTimestamp}</BreakAtDividers>,
      },
      {
        title: t('man-code.information-overlay.meta-data.reduced-version', 'Reduced Version'),
        entryId: 'reduced-version',
        value: manCode.reducedVersion ? t('common.label.yes') : t('common.label.no'),
      },
      {
        title: t('man-code.information-overlay.meta-data.size', 'Size'),
        entryId: 'size',
        value: formatByteSize(sizeInBytes),
      },
    ],
  }
}

function useContentInfo(
  manCode: CompleteManCode,
  storedConfiguration: ConfigurationStorageDataV1 | ConfigurationStorageDataV2 | undefined,
): InfoGroup {
  const { t } = useTranslation('shared')

  return {
    title: t('man-code.information-overlay.content.title', 'Content'),
    groupId: 'content',
    entries: [
      {
        title: t('man-code.information-overlay.content.factory-fpc-codes', 'Factory FPC Codes'),
        entryId: 'factory-fpc-codes',
        value: String(manCode.configuration.factoryFPCCodes?.length ?? 0),
      },
      {
        title: t('man-code.information-overlay.content.national-fpc-codes', 'National FPC Codes'),
        entryId: 'national-fpc-codes',
        value: String(manCode.configuration.nationalFPCCodes?.length ?? 0),
      },
      {
        title: t('man-code.information-overlay.content.changes', 'Changes'),
        entryId: 'changes',
        value: String(storedConfiguration?.history?.length ?? 0),
      },
    ],
  }
}

function useDataInfo(manCode: CompleteManCode): InfoGroup {
  const { t } = useTranslation('shared')

  return {
    title: t('man-code.information-overlay.data.title', 'Data'),
    groupId: 'data',
    entries: [
      {
        title: t('man-code.information-overlay.data.central-data-version', 'Central Data Version'),
        entryId: 'central-data-version',
        value: typeof manCode.configuration.centralDataVersion === 'number' ? String(manCode.configuration.centralDataVersion) : undefined,
      },
      {
        title: t('man-code.information-overlay.data.national-data-version', 'National Data Version'),
        entryId: 'national-data-version',
        value: undefined,
      },
    ],
  }
}

function useAdditionalInfo(manCode: CompleteManCode): InfoGroup {
  const { t } = useTranslation('shared')

  return {
    title: t('man-code.information-overlay.additional-information.title', 'Additional Information'),
    groupId: 'additional-information',
    entries: [
      {
        entryId: 'market-denomination',
        title: t('man-code.information-overlay.additional-information.entries.market-denomination', 'Market Denomination'),
        value: manCode.configuration.vehicleDescription,
      },
      {
        entryId: 'overall-status',
        title: t('man-code.information-overlay.additional-information.entries.overall-status', 'Overall Status'),
        // MAYBE: use i18n for status translation
        value: manCode.configuration.overallStatus,
      },
      {
        entryId: 'was-desired-dealer-date-estimated',
        title: t('man-code.information-overlay.additional-information.entries.was-desired-dealer-date-estimated', 'Estimated Desired Dealer Date'),
        value: manCode.configuration.desiredDealerDateIsEstimate ? t('common.label.yes') : t('common.label.no'),
      },
    ],
  }
}

export function useManCodeInformation(manCode: EnhancedManCode) {
  const { t } = useTranslation('shared')
  if (!ensureManCode(manCode)) {
    throw new Error(
      t('man-code.information-overlay.message.incomplete-man-code', 'The MAN-Code {{manCode}} is incomplete.', { manCode: manCode.code }),
    )
  }

  const storedConfiguration = readInternalStoredData(manCode)

  const apiConfiguration: FullApiConfiguration = {
    manCode: manCode.code,
    mode: 'read-only',
    orderLanguage: manCode.configuration.orderLanguage,
    orderCountry: manCode.configuration.orderCountry,
    partPeriod: isValidPeriod(manCode.configuration.partPeriod) ? periodToString(manCode.configuration.partPeriod) : undefined,
    desiredDealerDate: isValidPricingDate(manCode.configuration.desiredDealerDate)
      ? pricingDateToString(manCode.configuration.desiredDealerDate)
      : undefined,
    market: manCode.configuration.ecmParameters.marketId as EnhancedMarketId,
    vehicleType: manCode.configuration.ecmParameters.vehicleTypeId as EnhancedVehicleTypeId,
    configurationType: manCode.configuration.ecmParameters.configurationTypeId as EnhancedConfigTypeId | undefined,
    view: manCode.configuration.ecmParameters.viewId as EnhancedViewId,
    language: manCode.configuration.ecmParameters.languageId as EnhancedLanguageId | undefined,
  }

  const apiGroup = useFullApiConfigurationInfo(apiConfiguration)
  const dataInfo = useDataInfo(manCode)
  const additionalInfo = useAdditionalInfo(manCode)
  const internalInfo = useMaicInternalInfo(storedConfiguration)
  const metaDataInfo = useMetaDataInfo(manCode)
  const contentInfo = useContentInfo(manCode, storedConfiguration)

  return [apiGroup, dataInfo, additionalInfo, internalInfo, metaDataInfo, contentInfo]
}
